import { ErrorState } from '@/components/ui/error-state'
import { LoadingState } from '@/components/ui/loading-state'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { GOOGLE_MAPS_API_KEY } from '@/config/constants'
import { useInitializeAuth } from '@/hooks/useAuth'
import { useDynamicFavicon } from '@/hooks/useDynamicFavicon'
import { queryClient } from '@/lib/queryClient'
import { QueryClientProvider } from '@tanstack/react-query'
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'
import { APIProvider } from '@vis.gl/react-google-maps'
import { GeneralSettingsAuthSync } from './providers/GeneralSettingsAuthSync'
import { ThemeProvider } from './providers/theme-provider'
import { router } from './routes/routes'

function AuthInitializer() {
	const { isLoading, isError, error, isAuthenticated, user } = useInitializeAuth()
	const currentPath = window.location.pathname
	const isLoginPage = currentPath === '/login'
	const shouldShowInitialLoader = isLoading && !isLoginPage && !isAuthenticated && !user

	// Only block the app on the first auth bootstrap.
	// Org/project context switches can trigger background auth refetches; keep UI mounted for smooth SPA transitions.
	if (shouldShowInitialLoader) {
		return (
			<LoadingState
				title='Initializing Workspace'
				description='Connecting to your IoT platform and loading device data...'
				containerClassName='min-h-screen min-w-screen bg-background overflow-hidden flex items-center justify-center'
			/>
		)
	}

	// Show error state for non-401 errors (401 errors are handled by router)
	// Don't show error on login page for 401 errors (expected when not authenticated)
	const isUnauthorizedError = error && 'status' in error && error.status === 401
	const shouldShowError = isError && !(isLoginPage && isUnauthorizedError)
	if (shouldShowError) {
		return (
			<ErrorState
				title='Cannot connect to Workspace'
				message='Unable to initialize the platform. Try refreshing the page.'
				showSupportEmail={false}
				containerClassName='min-h-screen min-w-screen bg-background overflow-hidden flex items-center justify-center'
			/>
		)
	}

	// Router handles all authentication checks and redirects
	return <RouterProvider router={router} />
}

export function App() {
	useDynamicFavicon()

	return (
		<ThemeProvider>
			<GeneralSettingsAuthSync />
			<TooltipProvider>
				<APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['geometry']}>
					<QueryClientProvider client={queryClient}>
						<AuthInitializer />
						{/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
						<Toaster position='bottom-right' />
					</QueryClientProvider>
				</APIProvider>
			</TooltipProvider>
		</ThemeProvider>
	)
}
